import {
  useRoles,
  useUpdateRole,
  useCreateRole,
  useDeleteRole,
} from "./useRoles";
import { useAllPermissions } from "./usePermissions";
import { Permission } from "../api/services/permissions/type";
import {
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
} from "../api/services/roles/type";

export interface RolePermissionMatrix {
  roles: Role[];
  permissions: Permission[];
  rolePermissions: Record<string, string[]>;
}

export const useRolesAndPermissions = () => {
  const {
    data: rolesData,
    isLoading: isRolesLoading,
    isError: isRolesError,
    error: rolesError,
  } = useRoles({ limit: 100 });

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    error: permissionsError,
  } = useAllPermissions();

  const updateRoleMutation = useUpdateRole();
  const createRoleMutation = useCreateRole();
  const deleteRoleMutation = useDeleteRole();

  const roles = rolesData?.roles || [];

  const rolePermissions: Record<string, string[]> = {};
  roles.forEach((role) => {
    rolePermissions[role.id] = role.permissions.map((p) => p.id);
  });

  const updateRolePermissions = async (
    roleId: string,
    permissionId: string,
    hasPermission: boolean,
  ) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    const currentPermissionIds = rolePermissions[roleId] || [];

    let updatedPermissionIds: string[];
    if (hasPermission) {
      updatedPermissionIds = currentPermissionIds.includes(permissionId)
        ? currentPermissionIds
        : [...currentPermissionIds, permissionId];
    } else {
      updatedPermissionIds = currentPermissionIds.filter(
        (id) => id !== permissionId,
      );
    }

    const updateData = {
      name: role.name,
      description: role.description,
      is_system_role: role.isSystemRole,
      permissionIds: updatedPermissionIds,
      sidebarMenuIds:
        role.sidebarMenuIds || role.sidebar_menus?.map((menu) => menu.id) || [],
    };

    return updateRoleMutation.mutateAsync({ roleId, data: updateData });
  };

  const createRole = async (data: CreateRoleRequest) => {
    return createRoleMutation.mutateAsync(data);
  };

  const updateRole = async (roleId: string, data: UpdateRoleRequest) => {
    return updateRoleMutation.mutateAsync({ roleId, data });
  };

  const deleteRole = async (roleId: string) => {
    return deleteRoleMutation.mutateAsync(roleId);
  };

  return {
    roles,
    permissions: permissions || [],
    rolePermissions,
    updateRolePermissions,
    createRole,
    updateRole,
    deleteRole,
    isLoading: isRolesLoading || isPermissionsLoading,
    isUpdating: updateRoleMutation.isPending,
    isCreating: createRoleMutation.isPending,
    isDeleting: deleteRoleMutation.isPending,
    isError: isRolesError || isPermissionsError,
    error: rolesError || permissionsError,
    updateError: updateRoleMutation.error,
    createError: createRoleMutation.error,
    deleteError: deleteRoleMutation.error,
  };
};
